<template>
  <v-dialog
    :value="true"
    max-width="760"
    persistent
    @click:outside="$emit('close')"
  >
    <v-card>
      <!-- Title row -->
      <v-card-title class="text-h6">
        {{ modalTitle }}
        <v-spacer />
        <v-btn icon :disabled="busy" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Horizontal step indicator -->
      <v-card-subtitle class="pb-0">
        <v-row align="center" no-gutters class="px-2 pb-2">
          <v-col cols="auto" class="d-flex align-center">
            <v-avatar
              size="28"
              :color="step === 1 ? 'primary' : 'grey darken-1'"
              class="white--text subtitle-2"
              >1</v-avatar
            >
            <span
              class="ml-2 caption"
              :class="
                step === 1 ? 'primary--text font-weight-bold' : 'grey--text'
              "
            >
              Upload file
            </span>
          </v-col>
          <v-col><v-divider class="mx-3" /></v-col>
          <v-col cols="auto" class="d-flex align-center">
            <v-avatar
              size="28"
              :color="step === 2 ? 'primary' : 'grey lighten-1'"
              class="white--text subtitle-2"
              >2</v-avatar
            >
            <span
              class="ml-2 caption"
              :class="
                step === 2 ? 'primary--text font-weight-bold' : 'grey--text'
              "
            >
              Review &amp; confirm
            </span>
          </v-col>
        </v-row>
      </v-card-subtitle>

      <v-divider />

      <!-- ── STEP 1 ─────────────────────────────────────── -->
      <v-card-text v-if="step === 1" class="pt-4">
        <div class="overline grey--text mb-2">Step 1 — Get the Template</div>
        <v-sheet outlined rounded class="pa-3 d-flex align-center mb-4">
          <v-icon color="green darken-1" class="mr-3">mdi-file-excel</v-icon>
          <div class="flex-grow-1">
            <div class="body-2 font-weight-medium">
              {{ templateFileInfo.name }}
            </div>
            <div class="caption grey--text">{{ templateFileInfo.desc }}</div>
          </div>
          <v-btn outlined small color="primary" @click="downloadTemplate">
            <v-icon small left>mdi-download</v-icon>Download Template
          </v-btn>
        </v-sheet>

        <div class="overline grey--text mb-2">
          Step 2 — Upload Your Filled Sheet
        </div>
        <div
          class="drop-zone rounded mb-4"
          :class="{
            'drop-zone--active': isDragOver,
            'drop-zone--error': fileError,
          }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="onDrop"
        >
          <template v-if="!uploadedFile">
            <v-icon size="40" color="amber darken-2">mdi-folder</v-icon>
            <div class="body-2 grey--text text--darken-1 mt-2">
              Drag &amp; drop your file here
            </div>
            <div class="caption grey--text">
              or
              <a
                href="#"
                class="primary--text"
                @click.prevent="$refs.fileInput.click()"
                >browse to upload</a
              >
            </div>
            <div class="caption grey--text mt-1">
              .xlsx files only · Max {{ maxFileLabel }} · Template or your own
              file
            </div>
          </template>
          <template v-else>
            <v-icon color="green darken-1">mdi-file-excel</v-icon>
            <div class="body-2 mt-1 font-weight-medium">
              {{ uploadedFile.name }}
            </div>
            <div class="caption grey--text">{{ fileSize }}</div>
            <v-btn text x-small color="error" class="mt-1" @click="clearFile">
              <v-icon x-small left>mdi-close</v-icon>Remove
            </v-btn>
          </template>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx"
          style="display: none"
          @change="onFileInputChange"
        />
        <div v-if="fileError" class="error--text caption mb-3">
          {{ fileError }}
        </div>

        <!-- Field purpose reference -->
        <v-sheet
          color="amber lighten-5"
          outlined
          rounded
          class="pa-3 caption mb-4"
          style="border-color: #ffb300 !important"
        >
          <template v-if="mode === 'zone'">
            <div class="mb-1">
              <strong>Zone mapping mode:</strong> Map units to zones using Unit
              # and Zone Name only.
            </div>
            <div class="mt-1">
              Units must already exist in the system. Missing zones will be
              created automatically on save.
            </div>
          </template>
          <template v-else>
            <div class="mb-2">
              <strong>Standalone (non-PMS) facilities only.</strong>
            </div>
            <div class="mb-1">
              <strong>Required for new units:</strong> Unit Number, Type, and
              Size.
            </div>
            <div class="mb-2">
              <strong>Required for existing units:</strong> Unit Number.
            </div>
            <ul class="pl-4 mb-2">
              <li>
                New Unit Number creates a unit; an existing Unit Number updates
                existing unit.
              </li>
              <li>Blank Zone Name saves the unit as Unassigned.</li>
              <li>
                Add a Name plus contact info to create a Smart Unit; omit it to
                add the unit only.
              </li>
            </ul>
            <div>
              <strong>Auto-applied:</strong> fuzzy zone matching, unit-type
              matching, phone/date formatting, anomaly flags.
            </div>
          </template>
        </v-sheet>
      </v-card-text>

      <!-- ── STEP 2 ─────────────────────────────────────── -->
      <v-card-text v-if="step === 2" class="pt-4">
        <!-- File-level error -->
        <v-alert v-if="fileError" type="error" dense text class="mb-4">
          {{ fileError }}
        </v-alert>

        <v-alert
          v-if="!fileError && rowsOverLimit > 0"
          type="warning"
          dense
          text
          class="mb-3"
        >
          This file has {{ previewRows.length }} rows, but only the first
          {{ maxUploadRows }} will be imported. The remaining
          {{ rowsOverLimit }} row{{ rowsOverLimit !== 1 ? "s" : "" }} will be
          skipped — split them into a separate upload.
        </v-alert>

        <!-- Server result: only shown when the save came back with errors, so
             the operator sees WHICH rows the backend rejected rather than a
             count in a toast. These are separate from the preview errors above:
             the preview blocks rows before they are sent, this is what the
             backend rejected out of the rows that were sent. -->
        <v-alert
          v-if="serverErrors.length > 0"
          type="warning"
          dense
          text
          class="mb-4"
        >
          <div class="font-weight-medium mb-1">
            {{ saveResult.successful.length }} row{{
              saveResult.successful.length !== 1 ? "s" : ""
            }}
            saved · {{ serverErrors.length }} rejected by the server
            <span v-if="saveResult.skipped.length > 0">
              · {{ saveResult.skipped.length }} skipped</span
            >
          </div>
          <ul class="caption pl-4 mb-0 server-error-list">
            <li v-for="(msg, i) in serverErrors" :key="i">{{ msg }}</li>
          </ul>
          <div class="caption mt-2">
            Fix these rows in the spreadsheet and re-upload — saved rows are
            updated in place, not duplicated.
          </div>
        </v-alert>

        <!-- Summary tiles -->
        <v-row v-if="!fileError" dense class="mb-3">
          <v-col
            v-for="tile in summaryTiles"
            :key="tile.key"
            cols="6"
            sm="4"
            md="auto"
            class="flex-grow-1"
          >
            <v-sheet
              outlined
              rounded
              class="pa-2 text-center summary-tile"
              :class="{
                'summary-tile--active': isTileActive(tile),
                'summary-tile--disabled': busy,
              }"
              role="button"
              :tabindex="busy ? -1 : 0"
              :aria-pressed="String(isTileActive(tile))"
              @click="toggleTile(tile)"
              @keydown.enter.prevent="toggleTile(tile)"
              @keydown.space.prevent="toggleTile(tile)"
            >
              <div
                class="text-h6 font-weight-bold"
                :class="{
                  'error--text': tile.error && tile.value > 0,
                  'warning--text': tile.warning && tile.value > 0,
                }"
              >
                {{ tile.value }}
              </div>
              <div class="caption grey--text">{{ tile.label }}</div>
            </v-sheet>
          </v-col>
        </v-row>

        <!-- Active filter indicator -->
        <v-row v-if="activeFilter && !fileError" no-gutters class="mb-2 px-1">
          <v-col class="caption grey--text">
            Showing {{ filteredPreviewRows.length }} of
            {{ previewRows.length }} rows —
            <span class="font-weight-medium">{{ activeFilterLabel }}</span>
            <v-btn
              x-small
              text
              color="primary"
              class="ml-1"
              @click="clearFilter"
            >
              Clear filter
            </v-btn>
          </v-col>
        </v-row>

        <!-- Anomaly hints -->
        <v-expansion-panels
          v-if="anomalies.length > 0 && !fileError"
          flat
          class="mb-3"
        >
          <v-expansion-panel
            style="border: 1px solid #fb8c00; border-radius: 4px"
          >
            <v-expansion-panel-header
              class="caption font-weight-medium orange--text text--darken-2 py-2"
            >
              <span>
                <v-icon small color="orange darken-2" class="mr-1"
                  >mdi-alert-circle-outline</v-icon
                >
                {{ anomalies.length }} data quality hint{{
                  anomalies.length !== 1 ? "s" : ""
                }}
                — review before saving
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <ul class="caption mt-1 pl-4">
                <li v-for="(hint, i) in anomalies" :key="i" class="mb-1">
                  {{ hint.message }}
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Select all toolbar -->
        <v-row
          v-if="previewRows.length > 0 && !fileError"
          align="center"
          no-gutters
          class="mb-1 px-1"
        >
          <v-col cols="auto">
            <v-checkbox
              :input-value="allSelected"
              :indeterminate="someSelected && !allSelected"
              :disabled="busy"
              hide-details
              dense
              class="mt-0 pt-0"
              @change="toggleSelectAll"
            />
          </v-col>
          <v-col class="caption grey--text">
            {{ visibleSelectedCount }} of
            {{ visibleSelectableRows.length }} rows selected
            <!-- With a filter on, the visible count differs from what Save
                 sends — spell out the total so the two never look contradictory. -->
            <span v-if="activeFilter">
              (filtered) · {{ selectedCount }} of
              {{ totalSelectableCount }} selected in total</span
            >
            <span v-if="errorCount > 0" class="ml-2 error--text">
              · {{ errorCount }} row{{ errorCount !== 1 ? "s" : "" }} blocked
              (fix in file to include)
            </span>
            <span v-if="skippedCount > 0" class="ml-2 blue-grey--text">
              · {{ skippedCount }} row{{ skippedCount !== 1 ? "s" : "" }}
              skipped (device assigned)
            </span>
            <span v-if="autoFixCount > 0" class="ml-2 green--text">
              · {{ autoFixCount }} auto-corrected
            </span>
          </v-col>
        </v-row>

        <!-- Review table -->
        <v-data-table
          v-if="previewRows.length > 0"
          :headers="activePreviewHeaders"
          :items="filteredPreviewRows"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          dense
          :items-per-page="10"
          class="mb-2 review-table"
        >
          <template #item.included="{ item }">
            <v-checkbox
              v-model="item.included"
              :disabled="blockedStatuses.includes(item.status) || busy"
              hide-details
              dense
              class="mt-0 pt-0"
              @change="onRowToggle"
            />
          </template>
          <template #item.rowNum="{ item }">
            <span class="caption grey--text">{{ item.rowNum }}</span>
          </template>
          <template #item.status="{ item }">
            <v-chip
              x-small
              :color="statusColor(item.status)"
              text-color="white"
            >
              {{ item.status }}
            </v-chip>
          </template>
          <template #item.errorMsg="{ item }">
            <span
              v-if="item.errorMsg"
              class="caption"
              :class="
                item.status === 'Error'
                  ? 'error--text'
                  : item.status === 'AutoFixed'
                  ? 'green--text'
                  : ''
              "
              >{{ item.errorMsg }}</span
            >
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider />

      <!-- Actions -->
      <v-card-actions>
        <v-btn v-if="step === 2" text :disabled="busy" @click="reUpload">
          <v-icon left>mdi-arrow-left</v-icon>Re-upload
        </v-btn>
        <v-spacer />
        <v-btn outlined :disabled="busy" @click="$emit('close')">Cancel</v-btn>
        <v-btn
          v-if="step === 1"
          color="primary"
          :loading="loading"
          :disabled="!uploadedFile || !!fileError || busy"
          @click="extractFile"
        >
          Extract<v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-if="step === 2 && !fileError"
          color="primary"
          :loading="saving"
          :disabled="selectedCount === 0 || busy"
          @click="saveUpload"
        >
          Save — {{ selectedCount }} row{{ selectedCount !== 1 ? "s" : "" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as XLSX from "xlsx";
import useAlerts from "@tod-ui/composables/useAlerts";
import {
  BULK_UPLOAD_ZONE_MAPPINGS,
  GET_FACILITY_ZONE_NAMES,
  GET_FACILITY_UNIT_NAMES,
  GET_FACILITY_UNIT_TYPES,
} from "./graphql";

import {
  FULL_TEMPLATE_COLUMNS,
  ZONE_TEMPLATE_COLUMNS,
  REQUIRED_COLUMNS,
  ZONE_MODE_REQUIRED_COLUMNS,
  MAX_FILE_BYTES,
  MAX_FILE_LABEL,
  MAX_UPLOAD_ROWS,
  BLOCKED_STATUSES,
  MODES,
  statusRank,
  rowMatchesFilter,
  normalizeCellText,
  bestZoneMatch,
  normalizePhone,
  normalizeDate,
  excelDateToIso,
  isValidPhone,
  isValidUnitSize,
  isValidEmail,
} from "./bulkUploadHelpers";

export default {
  name: "BulkUploadZoneModal",
  props: {
    facilityId: { type: String, required: true },
    // "full" — units + zones + renters. Non-PMS (Standalone) facilities only;
    // the backend rejects it for PMS facilities.
    // "zone" — map existing units to zones. Available to every facility.
    mode: {
      type: String,
      default: "full",
      validator: (value) => MODES.includes(value),
    },
  },
  setup() {
    const { addAlert } = useAlerts();
    return { addAlert };
  },
  data() {
    return {
      step: 1,
      uploadedFile: null,
      isDragOver: false,
      loading: false,
      saving: false,
      fileError: null,
      previewRows: [],
      anomalies: [],
      // Server response from the last save: { successful, skipped, errors }.
      saveResult: null,
      // Key of the summary tile currently acting as a filter (null = show all).
      activeFilter: null,
      sortBy: "status",
      sortDesc: false,
    };
  },
  computed: {
    fileSize() {
      if (!this.uploadedFile) return "";
      const kb = this.uploadedFile.size / 1024;
      return kb < 1024
        ? `${Math.round(kb)} KB`
        : `${(kb / 1024).toFixed(1)} MB`;
    },
    maxUploadRows() {
      return MAX_UPLOAD_ROWS;
    },
    maxFileLabel() {
      return MAX_FILE_LABEL;
    },
    // While a file is being read or saved every control is inert — a second
    // click would re-send the same rows.
    busy() {
      return this.loading || this.saving;
    },
    totalSelectableCount() {
      return this.previewRows.filter(
        (r) => !BLOCKED_STATUSES.includes(r.status)
      ).length;
    },
    serverErrors() {
      return this.saveResult?.errors ?? [];
    },
    blockedStatuses() {
      return BLOCKED_STATUSES;
    },
    rowsOverLimit() {
      return Math.max(0, this.previewRows.length - MAX_UPLOAD_ROWS);
    },
    selectedCount() {
      return this.previewRows.filter((r) => r.included).length;
    },
    errorCount() {
      return this.previewRows.filter((r) => r.status === "Error").length;
    },
    skippedCount() {
      return this.previewRows.filter((r) => r.status === "Skipped").length;
    },
    autoFixCount() {
      return this.previewRows.filter((r) => r.status === "AutoFixed").length;
    },
    // Select-all reflects and acts on the VISIBLE rows only, so with a filter on
    // it cannot silently toggle rows the operator cannot see.
    allSelected() {
      return (
        this.visibleSelectableRows.length > 0 &&
        this.visibleSelectedCount === this.visibleSelectableRows.length
      );
    },
    someSelected() {
      return (
        this.visibleSelectedCount > 0 &&
        this.visibleSelectedCount < this.visibleSelectableRows.length
      );
    },
    summaryTiles() {
      const rows = this.previewRows;
      const errCount = rows.filter((r) => r.status === "Error").length;
      const warnCount = rows.filter((r) => r.status === "Warning").length;
      const included = rows.filter((r) => r.included);
      const tiles = [
        { key: "all", label: "Rows found", value: rows.length },
        {
          key: "zone",
          label: "Zones to map",
          value: new Set(included.filter((r) => r.zone).map((r) => r.zone))
            .size,
        },
        { key: "error", label: "Errors", value: errCount, error: errCount > 0 },
        {
          key: "skipped",
          label: "Skipped (rented + device)",
          value: this.skippedCount,
          warning: this.skippedCount > 0,
        },
        {
          key: "warning",
          label: "Warnings",
          value: warnCount,
          warning: warnCount > 0,
        },
      ];
      if (this.mode !== "zone") {
        tiles.splice(2, 0, {
          key: "tenant",
          label: "Tenants to link",
          value: included.filter((r) => r.tenant).length,
        });
      }
      return tiles;
    },
    // Rows the table actually renders. Filtering is display-only — `included`
    // still drives what gets saved, so hiding a row never deselects it.
    filteredPreviewRows() {
      if (!this.activeFilter) return this.previewRows;
      return this.previewRows.filter((row) =>
        rowMatchesFilter(row, this.activeFilter)
      );
    },
    visibleSelectableRows() {
      return this.filteredPreviewRows.filter(
        (r) => !BLOCKED_STATUSES.includes(r.status)
      );
    },
    visibleSelectedCount() {
      return this.visibleSelectableRows.filter((r) => r.included).length;
    },
    activeFilterLabel() {
      const tile = this.summaryTiles.find((t) => t.key === this.activeFilter);
      return tile ? tile.label : null;
    },
    modalTitle() {
      return this.mode === "zone"
        ? "Zone Mapping Upload"
        : "Upload Units & Tenants";
    },
    templateFileInfo() {
      if (this.mode === "zone") {
        return {
          name: "SD_Zone_Mapping_Template.xlsx",
          desc: "2 columns · Unit # & Zone Name",
        };
      }
      return {
        name: "SD_Unit_Upload_Template.xlsx",
        desc: "10 columns · Unit # required, the rest optional",
      };
    },
    activeTemplateColumns() {
      return this.mode === "zone"
        ? ZONE_TEMPLATE_COLUMNS
        : FULL_TEMPLATE_COLUMNS;
    },
    activePreviewHeaders() {
      const base = [
        { text: "", value: "included", width: "44px", sortable: false },
        { text: "Row #", value: "rowNum", width: "60px" },
        { text: "Unit #", value: "unit" },
        { text: "Zone", value: "zone" },
      ];
      if (this.mode !== "zone") {
        base.push({ text: "Tenant", value: "tenant" });
      }
      base.push(
        {
          text: "Status",
          value: "status",
          // Severity order, not alphabetical — see STATUS_RANK.
          sort: (a, b) => statusRank(a) - statusRank(b),
        },
        { text: "Notes", value: "errorMsg" }
      );
      return base;
    },
  },
  methods: {
    // ── Template download ──────────────────────────────────────────────────────
    downloadTemplate() {
      const columns = this.activeTemplateColumns;
      let sampleRow;
      if (this.mode === "zone") {
        sampleRow = ["A101", "ABC Zone"];
      } else {
        sampleRow = [
          "A101",
          "Drive-Up",
          "10x10",
          "ABC Zone",
          "John",
          "Smith",
          "(555) 123-4567",
          "",
          "john@example.com",
          "2026-01-15",
        ];
      }
      const ws = XLSX.utils.aoa_to_sheet([columns, sampleRow]);
      ws["!cols"] = columns.map(() => ({ wch: 18 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Units");
      XLSX.writeFile(wb, this.templateFileInfo.name);
    },

    // ── File drop / input ──────────────────────────────────────────────────────
    onDrop(e) {
      this.isDragOver = false;
      this.setFile(e.dataTransfer?.files?.[0]);
    },
    onFileInputChange(e) {
      this.setFile(e.target?.files?.[0]);
      e.target.value = "";
    },
    setFile(file) {
      this.fileError = null;
      if (!file) return;
      if (!file.name.endsWith(".xlsx")) {
        this.fileError =
          "Only .xlsx files are supported. Download the template and re-upload.";
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.fileError = `File exceeds ${MAX_FILE_LABEL}. Split into smaller batches and re-upload.`;
        return;
      }
      this.uploadedFile = file;
    },
    clearFile() {
      this.uploadedFile = null;
      this.fileError = null;
    },

    // ── Main extract flow ──────────────────────────────────────────────────────
    async extractFile() {
      if (!this.uploadedFile) return;
      this.loading = true;
      this.fileError = null;
      this.previewRows = [];
      this.anomalies = [];
      this.resetTableView();

      try {
        // Fetch facility data and read file in parallel.
        // Unit types are only needed in full mode — zone mode never creates units.
        const [rawBuffer, zonesResult, unitsResult, unitTypesResult] =
          await Promise.all([
            this.readFileAsArrayBuffer(this.uploadedFile),
            this.$apollo.query({
              query: GET_FACILITY_ZONE_NAMES,
              variables: { id: this.facilityId },
              fetchPolicy: "network-only",
            }),
            this.$apollo.query({
              query: GET_FACILITY_UNIT_NAMES,
              variables: { facilityId: this.facilityId },
              fetchPolicy: "network-only",
            }),
            this.mode === "zone"
              ? Promise.resolve(null)
              : this.$apollo.query({
                  query: GET_FACILITY_UNIT_TYPES,
                  variables: { id: this.facilityId },
                  fetchPolicy: "network-only",
                }),
          ]);

        const facilityZones = zonesResult.data?.smartZones || [];
        const facilityUnits = unitsResult.data?.organizationDescendants || [];
        const facilityUnitTypes =
          unitTypesResult?.data?.organization?.unitTypes || [];

        // Parse XLSX once
        const wb = XLSX.read(new Uint8Array(rawBuffer), {
          type: "array",
          cellDates: true,
        });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          raw: false,
        });
        // Second pass keeps the underlying cell types. A date-typed cell renders
        // differently per locale ("7/20/26", "20.07.2026", …), so read the real
        // Date instead of trying to parse whatever Excel chose to display.
        const rawTyped = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          raw: true,
        });

        if (!raw || raw.length < 2) {
          throw new Error(
            "No data rows found. Fill out the template and re-upload."
          );
        }

        // Header matching is case-insensitive so "unit #"/"Unit #" both work.
        const fileHeaders = raw[0].map((h) => String(h).trim());
        const headerKeys = fileHeaders.map((h) => h.toLowerCase());

        const missingRequired = (
          this.mode === "zone" ? ZONE_MODE_REQUIRED_COLUMNS : REQUIRED_COLUMNS
        ).filter((c) => !headerKeys.includes(c.toLowerCase()));
        if (missingRequired.length > 0) {
          throw new Error(
            "One or more required columns are missing. Please download the " +
              `latest template and re-upload. Missing: ${missingRequired.join(
                ", "
              )}.`
          );
        }

        // ── Row-level parsing with all validations ─────────────────────────────
        const rows = this.parseRows({
          raw,
          rawTyped,
          headerKeys,
          facilityZones,
          facilityUnits,
          facilityUnitTypes,
        });
        if (rows.length === 0) {
          throw new Error(
            "No data rows found. Fill out the template and re-upload."
          );
        }

        // ── Anomaly detection (zero-cost, rule-based) ──────────────────────────
        this.anomalies = this.detectAnomalies(rows);
        this.previewRows = rows;
        this.step = 2;
      } catch (err) {
        this.fileError = err.message;
        this.step = 2;
      } finally {
        this.loading = false;
      }
    },

    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () =>
          reject(
            new Error(
              "File could not be read. Ensure it is not password-protected."
            )
          );
        reader.readAsArrayBuffer(file);
      });
    },

    // ── Row parsing (header-based + all validations) ───────────────────────────
    parseRows({
      raw,
      rawTyped,
      headerKeys,
      facilityZones,
      facilityUnits,
      facilityUnitTypes,
    }) {
      // Build case-insensitive zone name → display name map.
      const zoneNameMap = new Map(
        facilityZones.map((z) => [
          normalizeCellText(z.name).toLowerCase(),
          z.name,
        ])
      );
      const unitNameSet = new Set(
        facilityUnits.map((u) => normalizeCellText(u.name).toLowerCase())
      );

      // A unit that is occupied AND has a device is live — the backend refuses
      // to modify it, so flag the row here rather than letting the operator
      // think it saved. A device on a vacant unit is fine: adding the renter is
      // the normal next step once the hardware is installed.
      const liveUnits = new Set(
        facilityUnits
          .filter((u) => u.devices?.length && u.billingAccounts?.length)
          .map((u) => normalizeCellText(u.name).toLowerCase())
      );

      const col = (templateName) =>
        headerKeys.indexOf(templateName.toLowerCase());

      // Existing facility unit types, keyed by downcased name. A sheet type that
      // isn't here will be created on save (see resolve_unit_types/4 backend-side).
      const unitTypeNames = new Set(
        (facilityUnitTypes || []).map((t) =>
          normalizeCellText(t.name).toLowerCase()
        )
      );

      const seenUnits = new Set();
      const rows = [];

      for (let i = 1; i < raw.length; i++) {
        const r = raw[i];
        if (r.every((v) => String(v).trim() === "")) continue;

        const get = (name) => normalizeCellText(r[col(name)]);

        let unit = get("Unit #");
        let type = get("Unit Type");
        let size = get("Unit Size");
        let zone = get("Zone Name");
        const firstName = get("First Name");
        const lastName = get("Last Name");
        let phone = get("Phone (Primary)");
        let phoneSecondary = get("Phone (Secondary)");
        let email = get("Email");
        // Prefer the typed cell so Excel's display format is irrelevant.
        const typedMoveIn = excelDateToIso(
          (rawTyped?.[i] || [])[col("Move-In Date")]
        );
        let moveInDate = typedMoveIn || get("Move-In Date");

        const autoFixes = [];
        let dateError = null;

        // ── Auto-normalise phone / date (full mode only) ─────────────────────
        if (this.mode !== "zone") {
          const normPhone = normalizePhone(phone);
          const normPhone2 = normalizePhone(phoneSecondary);
          const normDate = normalizeDate(moveInDate);

          if (normPhone !== phone && normPhone) {
            autoFixes.push(`Phone → ${normPhone}`);
            phone = normPhone;
          }
          if (normPhone2 !== phoneSecondary && normPhone2) {
            autoFixes.push(`Phone 2 → ${normPhone2}`);
            phoneSecondary = normPhone2;
          }
          if (moveInDate && !normDate) {
            dateError =
              `Move-In Date '${moveInDate}' is not valid. ` +
              "Use YYYY-MM-DD (or MM/DD/YYYY).";
          } else if (normDate && normDate !== moveInDate) {
            autoFixes.push(`Date → ${normDate}`);
            moveInDate = normDate;
          }
        }

        const tenant = [firstName, lastName].filter(Boolean).join(" ") || "";
        const errors = [];
        const warnings = [];
        let blocked = null;

        // ── Required fields ──────────────────────────────────────────────────
        if (!unit) errors.push("Unit # is required.");
        // Zone Name is required only in zone mode; in full mode a blank zone
        // saves the unit as Unassigned.
        if (!zone && this.mode === "zone")
          errors.push("Zone Name is required.");
        else if (zone && zone.length > 100)
          errors.push("Zone Name must be ≤100 characters.");

        // ── Duplicate unit # ─────────────────────────────────────────────────
        if (unit) {
          const key = unit.toLowerCase();
          if (seenUnits.has(key)) {
            errors.push(
              `Duplicate Unit # '${unit}' — must appear once per file.`
            );
          } else {
            seenUnits.add(key);
          }
        }

        if (this.mode === "zone") {
          // ── Zone mode: unit must exist, zone will be created if missing ────
          if (errors.length === 0 && unit) {
            const unitExists = unitNameSet.has(unit.toLowerCase());
            if (!unitExists) {
              errors.push(
                `Unit '${unit}' not found — create this unit in Manage Facility first.`
              );
            }
          }
          // Zone cross-reference
          if (errors.length === 0 && zone) {
            const zoneLower = zone.toLowerCase();
            if (!zoneNameMap.has(zoneLower)) {
              const { name: bestMatch, score: bestScore } = bestZoneMatch(
                zoneLower,
                zoneNameMap
              );
              if (bestMatch && bestScore >= 0.75) {
                warnings.push(
                  `Zone '${zone}' not found. Did you mean '${bestMatch}'? (${Math.round(
                    bestScore * 100
                  )}% match). Will create new zone if saved.`
                );
              } else {
                warnings.push(
                  `Zone '${zone}' not found — will be created on save.`
                );
              }
            }
          }
        } else {
          // ── Full mode (Standalone / non-PMS only): units, zones and renters
          //    are all created on the fly when missing. ──────────────────────

          // Unit cross-reference. A new unit needs a Type and Size so the
          // backend can resolve (or create) its unit_type record.
          if (
            errors.length === 0 &&
            unit &&
            liveUnits.has(unit.toLowerCase())
          ) {
            blocked =
              `Unit '${unit}' is rented and has a device assigned — ` +
              "it will not be updated.";
          } else if (errors.length === 0 && unit) {
            if (!unitNameSet.has(unit.toLowerCase())) {
              warnings.push(
                `Unit '${unit}' not found — will be created on save.`
              );
              if (!type) {
                errors.push("Unit Type is required when creating a new unit.");
              } else if (!size) {
                errors.push("Unit Size is required when creating a new unit.");
              } else if (!isValidUnitSize(size)) {
                errors.push(
                  `Unit Size '${size}' is not a valid format. Use dimensions like 10x10.`
                );
              } else if (!unitTypeNames.has(type.toLowerCase())) {
                warnings.push(
                  `Unit Type '${type}' is not yet used at this facility — it will be created on save.`
                );
              }
            }
          }

          if (errors.length === 0 && !zone) {
            warnings.push(
              "No Zone Name — the unit will be saved as Unassigned."
            );
          }

          // Zone cross-reference — fuzzy suggest if close match, else "will be created"
          if (errors.length === 0 && zone) {
            const zoneLower = zone.toLowerCase();
            if (!zoneNameMap.has(zoneLower)) {
              const { name: bestMatch, score: bestScore } = bestZoneMatch(
                zoneLower,
                zoneNameMap
              );
              if (bestMatch && bestScore >= 0.75) {
                warnings.push(
                  `Zone '${zone}' not found. Did you mean '${bestMatch}'? (${Math.round(
                    bestScore * 100
                  )}% match). Will create new zone if saved.`
                );
              } else {
                warnings.push(
                  `Zone '${zone}' not found — will be created on save.`
                );
              }
            }
          }

          // Format validations (warnings, optional fields)
          if (errors.length === 0) {
            if (!isValidEmail(email))
              warnings.push("Email format looks invalid.");
            if (!isValidPhone(phone))
              warnings.push("Phone (Primary) format looks invalid.");
            if (!isValidPhone(phoneSecondary))
              warnings.push("Phone (Secondary) format looks invalid.");
            if (dateError) errors.push(dateError);

            if (
              (phone || phoneSecondary || email || moveInDate) &&
              !firstName &&
              !lastName
            ) {
              // Blocking, not advisory: without a name no renter can be created,
              // so saving would create a bare unit and silently discard the
              // phone/email/move-in date the operator typed.
              errors.push(
                "First Name or Last Name is required when tenant details are provided."
              );
            } else if ((firstName || lastName) && !phone && !phoneSecondary) {
              // A responder needs a mobile contact method, so without a phone
              // the renter is created but the unit shows no recipient.
              warnings.push(
                "No phone given — the renter will be created but cannot receive alerts."
              );
            }
          }
        }

        // ── Determine row status ─────────────────────────────────────────────
        let status, errorMsg;
        if (blocked) {
          status = "Skipped";
          errorMsg = blocked;
        } else if (errors.length > 0) {
          status = "Error";
          errorMsg = errors[0];
        } else if (autoFixes.length > 0 && warnings.length === 0) {
          status = "AutoFixed";
          errorMsg = "Auto-corrected: " + autoFixes.join(", ");
        } else if (warnings.length > 0) {
          status = "Warning";
          errorMsg = warnings.join(" · ");
        } else if (autoFixes.length > 0) {
          status = "Warning";
          errorMsg =
            "Auto-corrected: " +
            autoFixes.join(", ") +
            " · " +
            warnings.join(" · ");
        } else {
          status = "Valid";
          errorMsg = "";
        }

        rows.push({
          rowNum: i + 1,
          unit,
          type,
          size,
          zone,
          firstName,
          lastName,
          phone,
          phoneSecondary,
          email,
          moveInDate,
          tenant,
          status,
          errorMsg,
          included: !BLOCKED_STATUSES.includes(status),
        });
      }

      return rows;
    },

    // ── Anomaly detection (zero-cost, rule-based) ──────────────────────────────
    detectAnomalies(rows) {
      const valid = rows.filter((r) => !BLOCKED_STATUSES.includes(r.status));
      if (valid.length < 3) return [];
      const hints = [];

      // Zone concentration
      const zoneCounts = {};
      valid.forEach((r) => {
        if (r.zone) zoneCounts[r.zone] = (zoneCounts[r.zone] || 0) + 1;
      });
      const topZone = Object.entries(zoneCounts).sort((a, b) => b[1] - a[1])[0];
      if (topZone && topZone[1] / valid.length > 0.7 && valid.length > 5) {
        hints.push({
          type: "warning",
          message: `${Math.round(
            (topZone[1] / valid.length) * 100
          )}% of rows map to zone "${topZone[0]}" — is this intentional?`,
        });
      }

      // All same move-in date
      const dates = valid.filter((r) => r.moveInDate).map((r) => r.moveInDate);
      if (dates.length > 5 && new Set(dates).size === 1) {
        hints.push({
          type: "warning",
          message: `All ${dates.length} rows share the same Move-In Date (${dates[0]}) — bulk import artifact?`,
        });
      }

      // Unit creation only happens in full mode — these hints don't apply to zone mode.
      if (this.mode !== "zone") {
        const types = valid.filter((r) => r.type).map((r) => r.type);
        if (types.length > 5 && new Set(types).size === 1) {
          hints.push({
            type: "info",
            message: `All ${types.length} units have the same Unit Type ("${types[0]}") — verify this is correct.`,
          });
        }

        const newUnits = rows.filter(
          (r) =>
            r.status === "Warning" &&
            r.errorMsg.includes("will be created on save") &&
            r.errorMsg.startsWith("Unit ")
        ).length;
        if (newUnits > 50) {
          hints.push({
            type: "warning",
            message: `${newUnits} new units will be created — large batch. Review carefully before saving.`,
          });
        }
      }

      // Rows with no tenant data at all (informational)
      const withTenant = valid.filter((r) => r.tenant).length;
      if (withTenant === 0 && valid.length > 0) {
        hints.push({
          type: "info",
          message: `No tenant data provided — rows will map units to zones only (no renter records created).`,
        });
      }

      return hints;
    },

    // ── Tile filters ───────────────────────────────────────────────────────────
    isTileActive(tile) {
      // "Rows found" is the unfiltered view, so it reads as active by default.
      return tile.key === "all"
        ? !this.activeFilter
        : this.activeFilter === tile.key;
    },
    toggleTile(tile) {
      if (this.busy) return;
      this.activeFilter = this.isTileActive(tile) ? null : tile.key;
    },
    clearFilter() {
      this.activeFilter = null;
    },

    // ── Select all / row toggle ────────────────────────────────────────────────
    toggleSelectAll(checked) {
      // Only the rows currently on screen — a filter must never toggle rows the
      // operator cannot see.
      this.visibleSelectableRows.forEach((row) => {
        row.included = !!checked;
      });
    },
    onRowToggle() {
      // Vue 2: explicit no-op allows @change binding without lint warning
    },

    reUpload() {
      this.step = 1;
      this.previewRows = [];
      this.fileError = null;
      this.uploadedFile = null;
      this.anomalies = [];
      this.resetTableView();
    },

    // Filter, sort and the last server result are per-file state — a new extract
    // starts clean.
    resetTableView() {
      this.activeFilter = null;
      this.sortBy = "status";
      this.sortDesc = false;
      this.saveResult = null;
    },

    // ── Save ───────────────────────────────────────────────────────────────────
    async saveUpload() {
      const selectedRows = this.previewRows.filter((r) => r.included);
      if (selectedRows.length === 0 || this.saving) return;
      this.saving = true;
      this.saveResult = null;

      try {
        const columns = this.activeTemplateColumns;
        const dataRows = selectedRows.map((r) => {
          if (this.mode === "zone") return [r.unit, r.zone];
          return [
            r.unit,
            r.type,
            r.size,
            r.zone,
            r.firstName,
            r.lastName,
            r.phone,
            r.phoneSecondary,
            r.email,
            r.moveInDate,
          ];
        });
        const ws = XLSX.utils.aoa_to_sheet([columns, ...dataRows]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Units");
        const wbOut = XLSX.write(wb, { type: "array", bookType: "xlsx" });
        const blob = new Blob([wbOut], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const filteredFile = new File([blob], "upload.xlsx", {
          type: blob.type,
        });

        const result = await this.$apollo.mutate({
          mutation: BULK_UPLOAD_ZONE_MAPPINGS,
          variables: {
            facilityId: this.facilityId,
            file: filteredFile,
            mode: this.mode,
          },
          context: { hasUpload: true },
        });

        const {
          successful = [],
          skipped = [],
          errors = [],
        } = result?.data?.bulkUploadZoneMappings ?? {};

        this.saveResult = { successful, skipped, errors };

        if (successful.length > 0) {
          let msg = `${successful.length} unit${
            successful.length !== 1 ? "s" : ""
          } mapped successfully.`;
          if (skipped.length > 0) msg += ` ${skipped.length} skipped.`;
          if (errors.length > 0)
            msg += ` ${errors.length} error${errors.length !== 1 ? "s" : ""}.`;
          this.addAlert({
            type: errors.length > 0 ? "warning" : "success",
            message: msg,
            timeout: 15,
          });
          // A partial save keeps the modal open so the server-side errors can be
          // read row by row — closing here would reduce them to a count in a
          // toast that disappears.
          if (errors.length === 0) this.$emit("close");
        } else if (skipped.length > 0 && errors.length === 0) {
          this.addAlert({
            type: "warning",
            message: `All ${skipped.length} unit${
              skipped.length !== 1 ? "s" : ""
            } skipped — not found in this facility, or rented with a device assigned.`,
          });
        } else {
          const detail = errors.slice(0, 3).join("; ");
          this.addAlert({
            type: "error",
            message: `Upload failed: ${detail}${
              errors.length > 3 ? ` (+${errors.length - 3} more)` : ""
            }.`,
          });
        }
      } catch (err) {
        console.error("BulkUploadZoneModal: save failed", err);
        // The resolver returns operator-readable messages for known failures
        // (PMS facility, missing columns, unreadable file) — surface them
        // instead of a generic string.
        const serverMessage =
          err?.graphQLErrors?.[0]?.message ||
          (err?.networkError ? null : err?.message);
        this.addAlert({
          type: "error",
          message: serverMessage
            ? `Save failed: ${serverMessage}`
            : "Save failed. Please try again or contact support.",
        });
      } finally {
        this.saving = false;
      }
    },

    statusColor(status) {
      if (status === "Valid") return "green";
      if (status === "Skipped") return "blue-grey";
      if (status === "Error") return "red";
      if (status === "Warning") return "orange";
      if (status === "AutoFixed") return "teal";
      return "grey";
    },
  },
};
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #bdbdbd;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
.drop-zone--active {
  border-color: #1976d2;
  background: #e3f2fd;
}
.drop-zone--error {
  border-color: #e53935;
  background: #fff5f5;
}
.review-table >>> .v-data-table__wrapper tr.v-data-table__empty-wrapper td {
  text-align: center;
}
.summary-tile {
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}
.summary-tile:hover {
  background: #f5f5f5;
}
.summary-tile:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}
.summary-tile--active {
  border-color: #1976d2 !important;
  background: #e3f2fd;
  box-shadow: inset 0 0 0 1px #1976d2;
}
.summary-tile--disabled {
  cursor: default;
  opacity: 0.6;
}
.server-error-list {
  max-height: 140px;
  overflow-y: auto;
}
</style>
